import { z } from "zod";

import { ClientBrainTemplate } from "./clientBrainTypes";

export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_TITLE_LENGTH = 80;
export const MAX_VALUE_LENGTH = 4000;

const descriptionSchema = z.string().max(MAX_DESCRIPTION_LENGTH);
const titleSchema = z.string().max(MAX_TITLE_LENGTH);

export const clientBrainFieldSchema = z.object({
    id: z.string().min(1),
    label: titleSchema,
    description: descriptionSchema,
    field_type: z.enum(["short_text", "long_text", "single_choice", "multi_choice", "checkbox", "date", "signature"]),
    options: z.array(titleSchema),
    required: z.boolean()
});

export const clientBrainSectionSchema = z.object({
    id: z.string().min(1),
    title: titleSchema,
    description: descriptionSchema,
    kind: z.enum(["fields", "goals", "risks"]),
    fields: z.array(clientBrainFieldSchema)
});

export const clientBrainTemplateSchema = z.object({
    id: z.string().min(1),
    title: titleSchema,
    sections: z.array(clientBrainSectionSchema)
});

export function clampDescription(value: string): string {
    return value.slice(0, MAX_DESCRIPTION_LENGTH);
}

export function clampTitle(value: string): string {
    return value.slice(0, MAX_TITLE_LENGTH);
}

export function clampValue(value: string): string {
    return value.slice(0, MAX_VALUE_LENGTH);
}

export function validateClientBrainTemplate(template: ClientBrainTemplate | null): string | null {
    if (template === null) {
        return null;
    }

    const parsed = clientBrainTemplateSchema.safeParse(template);
    if (!parsed.success) {
        return parsed.error.issues[0]?.message ?? "Invalid client brain template";
    }

    const seen = new Set<string>();

    for (const section of parsed.data.sections) {
        if (seen.has(section.id)) {
            return `Duplicate section id: ${section.id}`;
        }
        seen.add(section.id);

        for (const field of section.fields) {
            if (seen.has(field.id)) {
                return `Duplicate field id: ${field.id}`;
            }
            seen.add(field.id);
        }
    }

    return null;
}
